/**
 * BookmarkButton Component
 * Bouton moderne pour toggle les bookmarks avec animations et feedback
 */

"use client"

import { Button } from "@/components/ui/Button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { Bookmark, BookmarkCheck } from "lucide-react"
import { useEffect, useState } from "react"
import { useBookmarkToggle } from "../hooks/useBookmarkToggle"
import type { BookmarkToggleRequest } from "../types/bookmark.types"

interface BookmarkButtonProps {
  entityType: string
  entityId: string
  entityTitle: string
  metadata?: Record<string, any>
  userEmail?: string
  isBookmarked?: boolean
  variant?: "default" | "icon" | "outline" | "ghost"
  size?: "sm" | "default" | "lg" | "icon"
  className?: string
  showLabel?: boolean
  onToggleSuccess?: (bookmarked: boolean) => void
}

export default function BookmarkButton({
  entityType,
  entityId,
  entityTitle,
  metadata,
  userEmail,
  isBookmarked: initialBookmarked = false,
  variant = "ghost",
  size = "icon",
  className,
  showLabel = false,
  onToggleSuccess,
}: BookmarkButtonProps) {
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked)
  const [isAnimating, setIsAnimating] = useState(false)

  // Sync local state with prop when it changes (from context)
  useEffect(() => {
    setIsBookmarked(initialBookmarked)
  }, [initialBookmarked])

  const { toggle, isToggling } = useBookmarkToggle({
    userEmail,
    onSuccess: (bookmarked) => {
      setIsBookmarked(bookmarked)
      setIsAnimating(true)
      setTimeout(() => setIsAnimating(false), 600)
      onToggleSuccess?.(bookmarked)
    },
    onError: (error) => {
      console.error("Bookmark toggle error:", {
        error,
        message: error.message,
        code: error.code,
        details: error.details,
      })

      // Show error message to user
      alert(`Error: ${error.message || "Unable to save favourite"}`)
    },
  })

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (isToggling || !userEmail) return

    const request: BookmarkToggleRequest = {
      entityType,
      entityId,
      entityTitle,
      metadata,
    }

    await toggle(request)
  }

  const Icon = isBookmarked ? BookmarkCheck : Bookmark

  // Map "icon" variant to "ghost" variant with "icon" size
  const buttonVariant = variant === "icon" ? "ghost" : variant
  const buttonSize = variant === "icon" ? "icon" : size

  // Tooltip text based on state
  const tooltipText = isToggling
    ? "Saving..."
    : isBookmarked
    ? "Remove from favourites"
    : "Add to favourites"

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={buttonVariant}
          size={buttonSize}
          onClick={handleClick}
          disabled={isToggling || !userEmail}
          className={cn(
            "transition-all duration-200 border-none",
            isBookmarked &&
              "bg-primary/10 text-orange-400 hover:bg-primary/20 border-primary/50",
            isAnimating && "scale-110",
            className
          )}
          aria-label={
            isBookmarked
              ? `Remove ${entityTitle} from favourites`
              : `Add ${entityTitle} to favourites`
          }
          aria-pressed={isBookmarked}
        >
          <Icon
            className={cn(
              "h-4 w-4 transition-all duration-200",
              isBookmarked && "fill-current",
              isAnimating && "animate-pulse"
            )}
          />
          {showLabel && (
            <span className="ml-2 font-medium sr-only">
              {isBookmarked ? "Saved" : "Save"}
            </span>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={4}>
        <p>{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  )
}
